import {
  LitElement,
  html,
  css,
  unsafeHTML,
  ref,
  createRef,
  paths,
  t,
  styles,
} from '@hh/global.js'

class HHSlider extends LitElement {
  static properties = {
    active: {
      reflect: true,
    },
    breakpoint: {
      type: Number,
    },
    _current: {
      state: true,
    },
    vertical: {
      reflect: true,
    },
    dots: {
      reflect: true,
    },
    _advance: {
      state: true,
    },
    _scrollDirection: {
      state: true,
    },
    _direction: {
      state: true,
    },
    'slide-width': {
      reflect: true,
    },
    reverse: {
      reflect: true,
    },
    _offsetIndex: {
      state: true,
    },
    _offsetIndexMobile: {
      state: true,
    },
    _offsetBreakpoint: {
      state: true,
    }
  }

  $container = createRef()
  $next = createRef()
  $prev = createRef()

  constructor() {
    super()

    this._current = 1
    this._advance = 0

    this.updateUI = this.updateUI.bind(this)
    this.scrollTo = this.scrollTo.bind(this)

    this.calculateOffsetIndex()
  }

  calculateOffsetIndex() {
    this._offsetIndex = Number(this.getAttribute('offsetIndex')) || 1
    this._offsetIndexMobile = Number(this.getAttribute('offsetIndexMobile')) || 1
    this._offsetBreakpoint = this.getAttribute('offsetBreakpoint') || '750'
  }

  connectedCallback() {
    super.connectedCallback()

    this.items = Array.from(this.querySelectorAll('[slot="item"]'))

    this.observer = new ResizeObserver((...args) => {
      this.activate(...args)
    })

    setTimeout(() => {
      this.updateUI()
      this.$container?.value?.addEventListener('scroll', this.updateUI)
      if (this.breakpoint) {
        this.observer.observe(document.body)
      }
      this.addEventListener('hh-slider:scroll-to', this.scrollTo)
      if (this['slide-width']) {
        const activeItemIndex = Array.from(
          this.querySelectorAll('[slot="item"]')
        ).findIndex((item) => item.classList.contains('is-active'))
        if (!~activeItemIndex) return
        this.move(null, activeItemIndex)
      }
    }, 0)
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.$container?.value?.removeEventListener('scroll', this.updateUI)
    this.removeEventListener('hh-slider:scroll-to', this.scrollTo)
    this.observer.disconnect()
  }

  get _total() {
    return this.activeItems.length
  }

  get activeItems() {
    return this.items.filter(item => !item.classList.contains('hidden'))
  }

  activate([first, ...all]) {
    const { contentRect: { width = 0 } = {} } = first || {}
    if (width < this.breakpoint && !this.active) {
      this.active = true
    } else if (width >= this.breakpoint && this.active) {
      this.active = false
    }
  }

  updateUI() {
    if (!this._total || !this.$container.value) {
      return
    }

    this._advance = this.isVertical()
      ? this.activeItems[0].clientHeight
      : this.activeItems[0].clientWidth
    this._scrollDirection = this.isVertical() ? 'scrollTop' : 'scrollLeft'
    this._direction = this.isVertical() ? 'top' : 'left'
    this._current =
      Math.round(this.$container.value[this._scrollDirection] / this._advance) +
        (window.matchMedia(`(min-width: ${this._offsetBreakpoint}px)`).matches ? this._offsetIndex: this._offsetIndexMobile)
  }

  move(event, index = false) {
    event?.preventDefault()
    if (!this.$container.value) return
    const reverseIndex = this.reverse === 'true' ? -1 : 1
    const slideScrollPosition =
      index !== false
        ? this._advance * index
        : event.currentTarget.name === 'next'
        ? this.$container.value[this._scrollDirection] + this._advance
        : this.$container.value[this._scrollDirection] - this._advance
    if (!this.active) {
      const mediaOffsetTop = this.items.find(
        (item) => Number(item.dataset.index) === index
      ).offsetTop
      return window.scrollTo({
        top: mediaOffsetTop,
        behavior: 'smooth',
      })
    }
    this.$container?.value?.scrollTo({
      [this._direction]: slideScrollPosition * reverseIndex,
      behavior: 'smooth'
    })
  }

  scrollTo(event) {
    this.move(null, event.detail.index)
  }

  isVertical() {
    return [true, 'true'].includes(this.vertical)
  }

  showDots() {
    return [true, 'true'].includes(this.dots)
  }

  renderDot(index) {
    let dot_class = ''
    if (index < this._current - 1) {
      dot_class = 'active'
    } else if (index === this._current - 1) {
      dot_class = 'active is-current'
    }
    return html`
      <li>
        <button
          class="${dot_class}"
          @click="${(event) => this.move(event, index)}"
        >
          <span class="visually-hidden">${index}</span>
        </button>
      </li>
    `
  }

  renderDots() {
    if (!this.showDots()) {
      return html``
    }
    return html`<div class="dots">
      <ul role="list">
        ${[...Array(this._total).keys()].map((index) => this.renderDot(index))}
      </ul>
    </div>`
  }

  render() {
    return html`
      <div class="container" ${ref(this.$container)} role="list">
        <slot name="item"></slot>
      </div>
      <div class="buttons">
        <div class="counter">
          <span class="counter--current">${this._current}</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">${t('accessibility.of', 'of')}</span>
          <span class="counter--total">${this._total ? this._total : ''}</span>
        </div>
        <button
          ${ref(this.$prev)}
          @click="${this.move}"
          type="button"
          class="button button--prev"
          name="previous"
          aria-label="${t('accessibility.previous_slide', 'Previous')}"
          ?disabled="${this._current === 1}"
        >
          ${unsafeHTML(paths.caret_slider)}
        </button>
        <button
          ${ref(this.$next)}
          @click="${this.move}"
          type="button"
          class="button button--next"
          name="next"
          aria-label="${t('accessibility.next_slide', 'Next')}"
          ?disabled="${this._current === this._total}"
        >
          ${unsafeHTML(paths.caret_slider)}
        </button>
      </div>
      ${this.renderDots()}
    `
  }

  static styles = [
    styles.visuallyHidden,
    css`
      :host([active='true']) {
        display: block;
        position: relative;
      }

      .container {
        width: var(--hh-container-width, 100%);
        display: var(--hh-container-flex);
        flex-wrap: var(--hh-container-flex-wrap);
        margin: var(--hh-container-margin);
        gap: var(--hh-gap, 0);
      }

      :host([active='true']) .container {
        display: flex;
        flex-wrap: nowrap;
        position: relative;
        overflow-x: auto;
        scroll-snap-type: x mandatory;
        scroll-behavior: smooth;
        -webkit-overflow-scrolling: touch;
        -ms-overflow-style: none; /* Internet Explorer 10+ */
        scrollbar-width: none; /* Firefox */
        scrollbar-height: none; /* Firefox */
      }

      :host([active='true']) .container::-webkit-scrollbar {
        width: 0; /* Remove scrollbar space */
        height: 0; /* Remove scrollbar space */
        background: transparent; /* Optional: just make scrollbar invisible */
        display: none;
      }

      :host([active='true'][slide-width='variable']) .container {
        gap: 1rem;
        padding-bottom: 1rem;
      }

      @media screen and (min-width: 750px) {
        :host([active='true'][slide-width='variable']) .container {
          gap: 5rem;
        }
      }

      :host([active='true'][reverse='true']) .container {
        flex-direction: row-reverse;
      }

      :host([active='false']) .container {
        gap: 0.5rem;
      }

      :host([active='true'][vertical='true']) .container {
        aspect-ratio: var(--hh-vertical-aspect-ratio);
        flex-wrap: wrap;
        overflow-y: auto;
        overflow-x: hidden;
        scroll-snap-type: y mandatory;
      }

      :host([active='true']) ::slotted(*) {
        flex: 1 0 100%;
        scroll-snap-align: start;
      }

      :host([active='true'][slide-width='variable']) ::slotted(*) {
        flex: 0 0 auto;
        display: flex;
        flex-direction: column;
      }

      :host([active='false']) ::slotted(*) {
        flex: 1 0 100%;
      }

      :host([active='false']) ::slotted(*:nth-child(3n + 2)),
      :host([active='false']) ::slotted(*:nth-child(3n + 3)) {
        flex: 1 0 calc(50% - 0.5rem);
      }

      .buttons {
        display: none;
        align-items: center;
        position: absolute;
        z-index: 2;
        right: 0;
        bottom: 0;
        border: var(--hh-button-border);
        background: var(--hh-button-background);
      }

      :host([buttons-position="middle"]) .buttons {
        position: unset;
      }

      :host([active='true'][buttons='true']) .buttons {
        display: flex;
      }

      @media screen and (max-width: 749px) {
        :host([hideButtonsMobile="true"]) .buttons {
          display: none !important;
        }
      }

      .dots {
        display: none;
        position: absolute;
        z-index: 3;
        bottom: 20px;
        left: 0;
        right: 0;
      }

      :host([vertical='true']) .dots {
        top: 50%;
        bottom: auto;
        right: auto;
        left: 10px;
        transform: translateY(-50%);
      }

      :host([active='true'][dots='true']) .dots {
        display: block;
      }

      .dots ul {
        list-style-type: none;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0;
        margin: 0;
      }

      :host([vertical='true']) .dots ul {
        display: block;
      }

      .dots li {
        margin: 0 5px;
      }

      :host([vertical='true']) .dots li {
        margin: 5px 0;
      }

      .dots button {
        background: none;
        width: 5px;
        height: 5px;
        padding: 0;
        border: 1px solid var(--hh-color-brand-tertiary);
        border-radius: 0;
        position: relative;
        z-index: 1;
      }

      .dots button.is-current {
        background: var(--hh-color-brand-tertiary);
        border-radius: 50%;
      }

      .counter {
        margin: 0 2rem;
      }

      :host([counter="false"]) .counter {
        display: none;
      }

      .button {
        width: var(--hh-button-width, 44px);
        height: var(--hh-button-height, 44px);
        cursor: pointer;
        color: var(--hh-color);
        border: none;
        background: var(--hh-button-background);
      }

      .button:disabled {
        pointer-events: none;
        opacity: var(--disabled-opacity, 1);
      }

      .button svg {
        height: 1.6rem;
      }

      .button--prev {
        margin-left: -0.2rem;
      }

      .button--prev svg {
        transform: rotate(180deg);
      }

      .button--next {
        margin-left: -0.2rem;
      }

      :host([buttons-position="middle"]) .button {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: var(--button-z-index, 1);
      }

      :host([buttons-position="middle"]) .button--prev {
        margin-left: 0;
        left: var(--button-offset, 1rem);
      }

      :host([buttons-position="middle"]) .button--next {
        margin-left: 0;
        right: var(--button-offset, 1rem);
      }

      :host([vertical='true'][dots-style='line']) .dots {
        top: unset;
        bottom: var(--hh-dots-line-bottom, 2rem);
        right: var(--hh-dots-line-right, -2.5rem);
        left: unset;
        transform: unset;
      }

      :host([vertical='true'][dots-style='line']) .dots ul {
        display: flex;
        flex-direction: column;
        max-height: var(--hh-dots-line-max-height, 24rem);
      }

      :host([vertical='true'][dots-style='line']) .dots li {
        margin: 0;
        flex-basis: 4rem;
        display: flex;
        flex-direction: column;
        width: var(--hh-dots-line-width, 0.1rem);
      }

      :host([vertical='true'][dots-style='line']) .dots button {
        flex: 1;
        position: relative;
        border: 0;
        border-radius: 0;
        width: 100%;
        height: 100%;
        background: var(--hh-dots-line-background, #dddad4);
      }

      :host([vertical='true'][dots-style='line']) .dots button.is-current {
        border-radius: 0;
        border: 0;
        background: var(--hh-dots-line-background, #dddad4);
      }

      :host([vertical='true'][dots-style='line']) .dots button::after {
        content: '';
        position: absolute;
        width: 100%;
        height: 0;
        top: 0;
        left: 0;
        background: var(--hh-dots-line-background-active, #000000);
        transition: height var(--hh-dots-line-duration, 200ms) ease;
      }

      :host([vertical='true'][dots-style='line']) .dots button.active::after {
        height: 100%;
      }

      @media (max-width: 749px) {
        :host([vertical='true'][dots-style='line']) .dots {
          top: unset;
          bottom: var(--hh-dots-line-bottom-mobile, 1.6rem);
          left: var(--hh-dots-line-left, 2rem);
          right: unset;
          transform: unset;
        }
      }
    `,
  ]
}

customElements.define('hh-slider', HHSlider)
